import { useState, useMemo, useEffect } from 'react';
import { X, Sparkles, Save, RotateCcw, Calendar, ChevronLeft, ChevronRight, Check, Zap, Tag, TrendingUp, Megaphone, Server, Rocket, CalendarDays, Copy, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { DateRangeEditType } from '../../services/forecasts.service';

// Event types for marking historical events
interface ForecastEvent {
  id: string;
  name: string;
  type: EventType;
  startDate: string;
  endDate: string;
  impactPercent: number; // -100 to +100
  notes?: string;
  applyToFuture?: boolean;
  futureDates?: string[]; // dates to apply this event pattern to
}

type EventType = 'SYSTEM_OUTAGE' | 'SALES_PROMOTION' | 'PRODUCT_LAUNCH' | 'HOLIDAY' | 'MARKETING_CAMPAIGN' | 'SEASONAL' | 'OTHER';

const EVENT_TYPES: { value: EventType; label: string; icon: typeof Tag; color: string; defaultImpact: number }[] = [
  { value: 'SYSTEM_OUTAGE', label: 'System Outage', icon: Server, color: 'red', defaultImpact: -50 },
  { value: 'SALES_PROMOTION', label: 'Sales Promotion', icon: Megaphone, color: 'green', defaultImpact: 30 },
  { value: 'PRODUCT_LAUNCH', label: 'Product Launch', icon: Rocket, color: 'purple', defaultImpact: 50 },
  { value: 'HOLIDAY', label: 'Holiday', icon: CalendarDays, color: 'blue', defaultImpact: -30 },
  { value: 'MARKETING_CAMPAIGN', label: 'Marketing Campaign', icon: TrendingUp, color: 'orange', defaultImpact: 25 },
  { value: 'SEASONAL', label: 'Seasonal Event', icon: Zap, color: 'teal', defaultImpact: 20 },
  { value: 'OTHER', label: 'Other', icon: Tag, color: 'gray', defaultImpact: 0 },
];

interface ForecastDataPoint {
  interval: string;
  predictedValue: number;
  actualValue?: number;
  upperBound?: number;
  lowerBound?: number;
  timestamp: string;
}

interface ForecastShapeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  forecastData: ForecastDataPoint[];
  forecastStartDate: string;
  forecastEndDate: string;
  onSave: (edits: {
    dateRangeEdits?: Array<{
      startDate: string;
      endDate: string;
      editType: DateRangeEditType;
      value: number;
      reason?: string;
    }>;
    edits?: Array<{
      timestamp: string;
      newValue: number;
      reason?: string;
    }>;
    applyToFutureDays?: boolean;
  }) => void;
  isSaving?: boolean;
}

type EditMode = 'single_day' | 'date_range' | 'day_of_week' | 'time_range' | 'events';

interface Pattern {
  name: string;
  description: string;
  editType: DateRangeEditType;
  value: number;
  targetDays?: number[]; // 0=Sunday, 6=Saturday
  targetHours?: { start: number; end: number };
}

export default function ForecastShapeEditor({
  isOpen,
  onClose,
  forecastData,
  forecastStartDate,
  forecastEndDate,
  onSave,
  isSaving = false,
}: ForecastShapeEditorProps) {
  // Edit mode state
  const [editMode, setEditMode] = useState<EditMode>('single_day');

  // Date selection state
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    return forecastStartDate || new Date().toISOString().split('T')[0];
  });
  const [startDate, setStartDate] = useState<string>(() => {
    return forecastStartDate || new Date().toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return forecastEndDate || new Date().toISOString().split('T')[0];
  });

  // Sync state with props when they change (e.g., switching forecasts)
  useEffect(() => {
    if (forecastStartDate) {
      setSelectedDate(forecastStartDate);
      setStartDate(forecastStartDate);
    }
    if (forecastEndDate) {
      setEndDate(forecastEndDate);
    }
    // Reset edit state when props change
    setPendingEdits([]);
    setSelectedIntervals([]);
  }, [forecastStartDate, forecastEndDate]);

  // Day of week selection (0=Sunday, 6=Saturday)
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<number[]>([]);

  // Time range selection
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(17);

  // Adjustment settings
  const [editType, setEditType] = useState<DateRangeEditType>(DateRangeEditType.ADJUST_BY_PERCENTAGE);
  const [adjustmentValue, setAdjustmentValue] = useState(20);
  const [editReason, setEditReason] = useState('');
  const [applyToFutureDays, setApplyToFutureDays] = useState(false);

  // Track pending edits
  const [pendingEdits, setPendingEdits] = useState<Array<{
    type: 'dateRange' | 'interval';
    description: string;
    startDate?: string;
    endDate?: string;
    editType: DateRangeEditType;
    value: number;
    reason?: string;
    timestamps?: string[];
  }>>([]);

  // Selected intervals for single day editing
  const [selectedIntervals, setSelectedIntervals] = useState<string[]>([]);

  // Event marking state
  const [events, setEvents] = useState<ForecastEvent[]>([]);
  const [newEvent, setNewEvent] = useState<Partial<ForecastEvent>>({
    type: 'OTHER',
    impactPercent: 0,
    startDate: forecastStartDate,
    endDate: forecastStartDate,
  });
  const [selectedFutureDates, setSelectedFutureDates] = useState<string[]>([]);

  // Get data for selected date
  const dayData = useMemo(() => {
    if (!forecastData || forecastData.length === 0) return [];

    return forecastData.filter(point => {
      if (!point.timestamp) return false;
      const pointDate = new Date(point.timestamp).toISOString().split('T')[0];
      return pointDate === selectedDate;
    }).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [forecastData, selectedDate]);

  // Get unique dates in forecast
  const forecastDates = useMemo(() => {
    if (!forecastData || forecastData.length === 0) return [];

    const dates = new Set<string>();
    forecastData.forEach(point => {
      if (point.timestamp) {
        dates.add(new Date(point.timestamp).toISOString().split('T')[0]);
      }
    });
    return Array.from(dates).sort();
  }, [forecastData]);

  // Calculate preview of changes
  const previewStats = useMemo(() => {
    let affectedIntervals = 0;
    let originalTotal = 0;
    let adjustedTotal = 0;

    if (editMode === 'single_day' && selectedIntervals.length > 0) {
      dayData.forEach(point => {
        if (selectedIntervals.includes(point.timestamp)) {
          affectedIntervals++;
          originalTotal += point.predictedValue;
          if (editType === DateRangeEditType.ADJUST_BY_PERCENTAGE) {
            adjustedTotal += point.predictedValue * (1 + adjustmentValue / 100);
          } else if (editType === DateRangeEditType.SET_VALUE) {
            adjustedTotal += adjustmentValue;
          } else if (editType === DateRangeEditType.MULTIPLY) {
            adjustedTotal += point.predictedValue * adjustmentValue;
          } else {
            adjustedTotal += point.predictedValue + adjustmentValue;
          }
        }
      });
    } else if (editMode === 'date_range' && startDate && endDate) {
      forecastData.forEach(point => {
        if (!point.timestamp) return;
        // Ensure timestamp is a string and extract date part
        const pointDate = new Date(point.timestamp).toISOString().split('T')[0];
        if (startDate && endDate && pointDate >= startDate && pointDate <= endDate) {
          affectedIntervals++;
          originalTotal += point.predictedValue;
          if (editType === DateRangeEditType.ADJUST_BY_PERCENTAGE) {
            adjustedTotal += point.predictedValue * (1 + adjustmentValue / 100);
          } else if (editType === DateRangeEditType.SET_VALUE) {
            adjustedTotal += adjustmentValue;
          } else if (editType === DateRangeEditType.MULTIPLY) {
            adjustedTotal += point.predictedValue * adjustmentValue;
          } else {
            adjustedTotal += point.predictedValue + adjustmentValue;
          }
        }
      });
    }

    const percentChange = originalTotal > 0 ? ((adjustedTotal - originalTotal) / originalTotal) * 100 : 0;

    return {
      affectedIntervals,
      originalTotal: Math.round(originalTotal),
      adjustedTotal: Math.round(adjustedTotal),
      percentChange: percentChange.toFixed(1),
    };
  }, [editMode, selectedIntervals, dayData, forecastData, startDate, endDate, editType, adjustmentValue]);

  // Predefined patterns
  const patterns: Pattern[] = [
    {
      name: 'Monday Morning Peak',
      description: '+50% volume 9am-11am on Mondays',
      editType: DateRangeEditType.ADJUST_BY_PERCENTAGE,
      value: 50,
      targetDays: [1],
      targetHours: { start: 9, end: 11 },
    },
    {
      name: 'Weekend Reduction',
      description: '-40% volume on weekends',
      editType: DateRangeEditType.ADJUST_BY_PERCENTAGE,
      value: -40,
      targetDays: [0, 6],
    },
    {
      name: 'Lunch Hour Spike',
      description: '+30% volume 11am-1pm',
      editType: DateRangeEditType.ADJUST_BY_PERCENTAGE,
      value: 30,
      targetHours: { start: 11, end: 13 },
    },
    {
      name: 'End of Day Drop',
      description: '-25% volume after 5pm',
      editType: DateRangeEditType.ADJUST_BY_PERCENTAGE,
      value: -25,
      targetHours: { start: 17, end: 24 },
    },
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Navigate dates
  const goToPreviousDate = () => {
    const currentIndex = forecastDates.indexOf(selectedDate);
    if (currentIndex > 0) {
      setSelectedDate(forecastDates[currentIndex - 1]);
      setSelectedIntervals([]);
    }
  };

  const goToNextDate = () => {
    const currentIndex = forecastDates.indexOf(selectedDate);
    if (currentIndex < forecastDates.length - 1) {
      setSelectedDate(forecastDates[currentIndex + 1]);
      setSelectedIntervals([]);
    }
  };

  // Toggle interval selection
  const toggleInterval = (timestamp: string) => {
    setSelectedIntervals(prev =>
      prev.includes(timestamp) ? prev.filter(t => t !== timestamp) : [...prev, timestamp]
    );
  };

  // Select all intervals in a time range
  const selectTimeRange = () => {
    const selected = dayData
      .filter(point => {
        const hour = new Date(point.timestamp).getHours();
        return hour >= startHour && hour < endHour;
      })
      .map(point => point.timestamp);
    setSelectedIntervals(selected);
  };

  // Add edit to pending list
  const addPendingEdit = () => {
    let description = '';
    let edit: typeof pendingEdits[0];

    if (editMode === 'single_day' && selectedIntervals.length > 0) {
      const dateStr = new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      description = `${dateStr}: ${selectedIntervals.length} intervals ${editType === DateRangeEditType.ADJUST_BY_PERCENTAGE ? (adjustmentValue >= 0 ? '+' : '') + adjustmentValue + '%' : editType === DateRangeEditType.SET_VALUE ? 'set to ' + adjustmentValue : (adjustmentValue >= 0 ? '+' : '') + adjustmentValue}`;

      edit = {
        type: 'interval',
        description,
        editType,
        value: adjustmentValue,
        reason: editReason,
        timestamps: selectedIntervals,
      };
    } else if (editMode === 'date_range') {
      // Guard against invalid dates
      if (!startDate || !endDate) {
        return;
      }
      const startDateObj = new Date(startDate + 'T00:00:00');
      const endDateObj = new Date(endDate + 'T00:00:00');
      if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
        return;
      }
      const start = startDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const end = endDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      description = `${start} - ${end}: ${editType === DateRangeEditType.ADJUST_BY_PERCENTAGE ? (adjustmentValue >= 0 ? '+' : '') + adjustmentValue + '%' : editType === DateRangeEditType.SET_VALUE ? 'set to ' + adjustmentValue : (adjustmentValue >= 0 ? '+' : '') + adjustmentValue}`;

      edit = {
        type: 'dateRange',
        description,
        startDate,
        endDate,
        editType,
        value: adjustmentValue,
        reason: editReason,
      };
    } else {
      return;
    }

    setPendingEdits(prev => [...prev, edit]);
    setSelectedIntervals([]);
    setEditReason('');
  };

  // Remove pending edit
  const removePendingEdit = (index: number) => {
    setPendingEdits(prev => prev.filter((_, i) => i !== index));
  };

  // Apply pattern
  const applyPattern = (pattern: Pattern) => {
    // For patterns, we need to find the matching intervals and create edits
    const matchingTimestamps: string[] = [];

    forecastData.forEach(point => {
      if (!point.timestamp) return;

      const date = new Date(point.timestamp);
      const dayOfWeek = date.getDay();
      const hour = date.getHours();

      let matches = true;

      if (pattern.targetDays && !pattern.targetDays.includes(dayOfWeek)) {
        matches = false;
      }

      if (pattern.targetHours) {
        if (hour < pattern.targetHours.start || hour >= pattern.targetHours.end) {
          matches = false;
        }
      }

      if (matches) {
        matchingTimestamps.push(point.timestamp);
      }
    });

    if (matchingTimestamps.length > 0) {
      const edit: typeof pendingEdits[0] = {
        type: 'interval',
        description: `Pattern: ${pattern.name} (${matchingTimestamps.length} intervals)`,
        editType: pattern.editType,
        value: pattern.value,
        reason: pattern.description,
        timestamps: matchingTimestamps,
      };
      setPendingEdits(prev => [...prev, edit]);
    }
  };

  // Save all pending edits
  const handleSave = () => {
    const dateRangeEdits: Array<{
      startDate: string;
      endDate: string;
      editType: DateRangeEditType;
      value: number;
      reason?: string;
    }> = [];

    const intervalEdits: Array<{
      timestamp: string;
      newValue: number;
      reason?: string;
    }> = [];

    pendingEdits.forEach(edit => {
      if (edit.type === 'dateRange' && edit.startDate && edit.endDate) {
        dateRangeEdits.push({
          startDate: edit.startDate,
          endDate: edit.endDate,
          editType: edit.editType,
          value: edit.value,
          reason: edit.reason,
        });
      } else if (edit.type === 'interval' && edit.timestamps) {
        // For interval edits, we need to calculate the new values
        edit.timestamps.forEach(timestamp => {
          const point = forecastData.find(p => p.timestamp === timestamp);
          if (point) {
            let newValue: number;
            if (edit.editType === DateRangeEditType.ADJUST_BY_PERCENTAGE) {
              newValue = Math.round(point.predictedValue * (1 + edit.value / 100));
            } else if (edit.editType === DateRangeEditType.SET_VALUE) {
              newValue = edit.value;
            } else if (edit.editType === DateRangeEditType.MULTIPLY) {
              newValue = Math.round(point.predictedValue * edit.value);
            } else {
              newValue = Math.round(point.predictedValue + edit.value);
            }
            intervalEdits.push({
              timestamp,
              newValue,
              reason: edit.reason,
            });
          }
        });
      }
    });

    onSave({
      dateRangeEdits: dateRangeEdits.length > 0 ? dateRangeEdits : undefined,
      edits: intervalEdits.length > 0 ? intervalEdits : undefined,
      applyToFutureDays,
    });
  };

  // Reset
  const reset = () => {
    setPendingEdits([]);
    setSelectedIntervals([]);
    setAdjustmentValue(20);
    setEditReason('');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Edit Forecast Shape</h2>
                  <p className="text-sm text-gray-500">
                    Adjust volume predictions by date, date range, or time intervals
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Edit Mode Tabs */}
              <div className="flex space-x-2 border-b border-gray-200 pb-4">
                <button
                  onClick={() => setEditMode('single_day')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    editMode === 'single_day'
                      ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Calendar className="h-4 w-4 inline mr-2" />
                  Single Day
                </button>
                <button
                  onClick={() => setEditMode('date_range')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    editMode === 'date_range'
                      ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Date Range
                </button>
                <button
                  onClick={() => setEditMode('day_of_week')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    editMode === 'day_of_week'
                      ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Quick Patterns
                </button>
                <button
                  onClick={() => setEditMode('events')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    editMode === 'events'
                      ? 'bg-indigo-100 text-indigo-700 border-b-2 border-indigo-600'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Tag className="h-4 w-4 inline mr-2" />
                  Mark Events
                </button>
              </div>

              {/* Single Day Mode */}
              {editMode === 'single_day' && (
                <div className="space-y-4">
                  {/* Date Navigation */}
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                    <button
                      onClick={goToPreviousDate}
                      disabled={forecastDates.indexOf(selectedDate) === 0}
                      className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>

                    <div className="flex items-center space-x-4">
                      <input
                        type="date"
                        value={selectedDate}
                        min={forecastStartDate}
                        max={forecastEndDate}
                        onChange={(e) => {
                          setSelectedDate(e.target.value);
                          setSelectedIntervals([]);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <span className="text-lg font-semibold text-gray-900">
                        {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                    </div>

                    <button
                      onClick={goToNextDate}
                      disabled={forecastDates.indexOf(selectedDate) === forecastDates.length - 1}
                      className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Time Range Quick Select */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-3">Quick Select Time Range</h4>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-blue-700">From:</label>
                        <select
                          value={startHour}
                          onChange={(e) => setStartHour(parseInt(e.target.value))}
                          className="px-3 py-1.5 border border-blue-300 rounded-md text-sm"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-blue-700">To:</label>
                        <select
                          value={endHour}
                          onChange={(e) => setEndHour(parseInt(e.target.value))}
                          className="px-3 py-1.5 border border-blue-300 rounded-md text-sm"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={selectTimeRange}
                        className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                      >
                        Select Range
                      </button>
                      <button
                        onClick={() => setSelectedIntervals(dayData.map(p => p.timestamp))}
                        className="px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-200"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedIntervals([])}
                        className="px-4 py-1.5 text-gray-600 text-sm font-medium rounded-md hover:bg-gray-100"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Interval Grid */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Select Intervals ({selectedIntervals.length} selected)
                      </h4>
                      <span className="text-xs text-gray-500">Click to select/deselect</span>
                    </div>
                    <div className="grid grid-cols-8 gap-2 max-h-64 overflow-y-auto">
                      {dayData.map((point) => (
                        <button
                          key={point.timestamp}
                          onClick={() => toggleInterval(point.timestamp)}
                          className={`p-2 rounded text-xs font-medium transition-all ${
                            selectedIntervals.includes(point.timestamp)
                              ? 'bg-indigo-600 text-white ring-2 ring-indigo-400'
                              : 'bg-white text-gray-700 border border-gray-300 hover:border-indigo-400'
                          }`}
                        >
                          <div className="truncate">{point.interval}</div>
                          <div className="font-bold">{point.predictedValue}</div>
                        </button>
                      ))}
                      {dayData.length === 0 && (
                        <div className="col-span-8 text-center py-8 text-gray-500">
                          No data points for this date
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Date Range Mode */}
              {editMode === 'date_range' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Select Date Range</h4>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">Start:</label>
                        <input
                          type="date"
                          value={startDate}
                          min={forecastStartDate}
                          max={forecastEndDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-700">End:</label>
                        <input
                          type="date"
                          value={endDate}
                          min={startDate}
                          max={forecastEndDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Day of Week Filter */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Filter by Day of Week (optional)</h4>
                    <div className="flex space-x-2">
                      {dayNames.map((day, index) => (
                        <button
                          key={day}
                          onClick={() => {
                            setSelectedDaysOfWeek(prev =>
                              prev.includes(index) ? prev.filter(d => d !== index) : [...prev, index]
                            );
                          }}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                            selectedDaysOfWeek.includes(index)
                              ? 'bg-indigo-600 text-white'
                              : 'bg-white text-gray-700 border border-gray-300 hover:border-indigo-400'
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                      {selectedDaysOfWeek.length > 0 && (
                        <button
                          onClick={() => setSelectedDaysOfWeek([])}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Patterns Mode */}
              {editMode === 'day_of_week' && (
                <div className="space-y-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-purple-900 mb-3">Quick Pattern Templates</h4>
                    <p className="text-xs text-purple-700 mb-4">
                      Apply common adjustment patterns across your entire forecast
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {patterns.map((pattern) => (
                        <button
                          key={pattern.name}
                          onClick={() => applyPattern(pattern)}
                          className="p-4 text-left bg-white border border-purple-300 rounded-lg hover:bg-purple-100 transition-colors"
                        >
                          <div className="font-medium text-sm text-purple-900">{pattern.name}</div>
                          <div className="text-xs text-purple-700 mt-1">{pattern.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Events Mode - Mark and Label Historical Events */}
              {editMode === 'events' && (
                <div className="space-y-6">
                  {/* Event Type Selection */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-amber-900 mb-3">Mark Historical Event</h4>
                    <p className="text-xs text-amber-700 mb-4">
                      Label days with events that affected contact volume. These patterns can be applied to future dates.
                    </p>

                    {/* Event Type Grid */}
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {EVENT_TYPES.map((eventType) => {
                        const Icon = eventType.icon;
                        const isSelected = newEvent.type === eventType.value;
                        const colorClasses: Record<string, string> = {
                          red: isSelected ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-300 hover:bg-red-50',
                          green: isSelected ? 'bg-green-600 text-white border-green-600' : 'bg-white text-green-700 border-green-300 hover:bg-green-50',
                          purple: isSelected ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50',
                          blue: isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50',
                          orange: isSelected ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50',
                          teal: isSelected ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-teal-700 border-teal-300 hover:bg-teal-50',
                          gray: isSelected ? 'bg-gray-600 text-white border-gray-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                        };
                        return (
                          <button
                            key={eventType.value}
                            onClick={() => setNewEvent(prev => ({
                              ...prev,
                              type: eventType.value,
                              impactPercent: eventType.defaultImpact,
                            }))}
                            className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${colorClasses[eventType.color]}`}
                          >
                            <Icon className="h-5 w-5" />
                            <span className="text-xs font-medium text-center">{eventType.label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Event Details */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-amber-700 mb-1">Event Name</label>
                        <input
                          type="text"
                          value={newEvent.name || ''}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g., Black Friday Sale"
                          className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-amber-700 mb-1">
                          Volume Impact: {newEvent.impactPercent && newEvent.impactPercent >= 0 ? '+' : ''}{newEvent.impactPercent || 0}%
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="range"
                            min="-100"
                            max="200"
                            value={newEvent.impactPercent || 0}
                            onChange={(e) => setNewEvent(prev => ({ ...prev, impactPercent: parseInt(e.target.value) }))}
                            className="flex-1 h-2 bg-amber-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                          />
                          <span className={`text-sm font-bold w-16 text-right ${
                            (newEvent.impactPercent || 0) > 0 ? 'text-green-600' :
                            (newEvent.impactPercent || 0) < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {newEvent.impactPercent && newEvent.impactPercent >= 0 ? '+' : ''}{newEvent.impactPercent || 0}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Date Range */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs font-medium text-amber-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={newEvent.startDate || forecastStartDate}
                          min={forecastStartDate}
                          max={forecastEndDate}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, startDate: e.target.value, endDate: prev.endDate && prev.endDate < e.target.value ? e.target.value : prev.endDate }))}
                          className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-amber-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={newEvent.endDate || forecastStartDate}
                          min={newEvent.startDate || forecastStartDate}
                          max={forecastEndDate}
                          onChange={(e) => setNewEvent(prev => ({ ...prev, endDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-amber-700 mb-1">Notes (optional)</label>
                      <textarea
                        value={newEvent.notes || ''}
                        onChange={(e) => setNewEvent(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Additional details about this event..."
                        rows={2}
                        className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    {/* Add Event Button */}
                    <button
                      onClick={() => {
                        if (!newEvent.name || !newEvent.type || !newEvent.startDate || !newEvent.endDate) {
                          return;
                        }
                        const event: ForecastEvent = {
                          id: `event-${Date.now()}`,
                          name: newEvent.name,
                          type: newEvent.type,
                          startDate: newEvent.startDate,
                          endDate: newEvent.endDate,
                          impactPercent: newEvent.impactPercent || 0,
                          notes: newEvent.notes,
                        };
                        setEvents(prev => [...prev, event]);
                        setNewEvent({
                          type: 'OTHER',
                          impactPercent: 0,
                          startDate: forecastStartDate,
                          endDate: forecastStartDate,
                        });
                      }}
                      disabled={!newEvent.name || !newEvent.startDate || !newEvent.endDate}
                      className={`w-full py-2 text-sm font-medium rounded-lg transition-colors ${
                        newEvent.name && newEvent.startDate && newEvent.endDate
                          ? 'bg-amber-600 text-white hover:bg-amber-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Add Event to List
                    </button>
                  </div>

                  {/* Saved Events List */}
                  {events.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">
                        Marked Events ({events.length})
                      </h4>
                      <div className="space-y-3 max-h-60 overflow-y-auto">
                        {events.map((event) => {
                          const eventTypeInfo = EVENT_TYPES.find(t => t.value === event.type);
                          const Icon = eventTypeInfo?.icon || Tag;
                          const colorClasses: Record<string, string> = {
                            red: 'bg-red-100 text-red-800 border-red-200',
                            green: 'bg-green-100 text-green-800 border-green-200',
                            purple: 'bg-purple-100 text-purple-800 border-purple-200',
                            blue: 'bg-blue-100 text-blue-800 border-blue-200',
                            orange: 'bg-orange-100 text-orange-800 border-orange-200',
                            teal: 'bg-teal-100 text-teal-800 border-teal-200',
                            gray: 'bg-gray-100 text-gray-800 border-gray-200',
                          };
                          return (
                            <div
                              key={event.id}
                              className={`p-3 rounded-lg border ${colorClasses[eventTypeInfo?.color || 'gray']}`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3">
                                  <Icon className="h-5 w-5 mt-0.5" />
                                  <div>
                                    <div className="font-medium text-sm">{event.name}</div>
                                    <div className="text-xs mt-1">
                                      {new Date(event.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                      {event.startDate !== event.endDate && (
                                        <> - {new Date(event.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>
                                      )}
                                      <span className={`ml-2 font-semibold ${event.impactPercent >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                        {event.impactPercent >= 0 ? '+' : ''}{event.impactPercent}% impact
                                      </span>
                                    </div>
                                    {event.notes && (
                                      <div className="text-xs mt-1 opacity-75">{event.notes}</div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      // Apply event as a pending edit
                                      const edit: typeof pendingEdits[0] = {
                                        type: 'dateRange',
                                        description: `Event: ${event.name} (${event.impactPercent >= 0 ? '+' : ''}${event.impactPercent}%)`,
                                        startDate: event.startDate,
                                        endDate: event.endDate,
                                        editType: DateRangeEditType.ADJUST_BY_PERCENTAGE,
                                        value: event.impactPercent,
                                        reason: `${eventTypeInfo?.label}: ${event.name}${event.notes ? ' - ' + event.notes : ''}`,
                                      };
                                      setPendingEdits(prev => [...prev, edit]);
                                    }}
                                    className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                    title="Apply to forecast"
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setEvents(prev => prev.filter(e => e.id !== event.id))}
                                    className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Remove event"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Apply to Future Dates */}
                  {events.length > 0 && (
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Copy className="h-5 w-5 text-indigo-600" />
                        <h4 className="text-sm font-semibold text-indigo-900">Apply Event Pattern to Future Dates</h4>
                      </div>
                      <p className="text-xs text-indigo-700 mb-4">
                        Select future dates where you expect similar events to occur. The same volume impact will be applied.
                      </p>

                      {/* Future Date Selection */}
                      <div className="grid grid-cols-7 gap-1 mb-4">
                        {forecastDates.map((date) => {
                          const isSelected = selectedFutureDates.includes(date);
                          const hasEvent = events.some(e => date >= e.startDate && date <= e.endDate);
                          const dayOfWeek = new Date(date + 'T00:00:00').getDay();
                          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                          return (
                            <button
                              key={date}
                              onClick={() => {
                                if (hasEvent) return; // Can't select dates that already have events
                                setSelectedFutureDates(prev =>
                                  prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
                                );
                              }}
                              disabled={hasEvent}
                              className={`p-1.5 rounded text-xs font-medium transition-all ${
                                hasEvent
                                  ? 'bg-amber-200 text-amber-800 cursor-not-allowed'
                                  : isSelected
                                  ? 'bg-indigo-600 text-white'
                                  : isWeekend
                                  ? 'bg-gray-100 text-gray-600 hover:bg-indigo-100'
                                  : 'bg-white text-gray-700 border border-gray-200 hover:border-indigo-400'
                              }`}
                              title={hasEvent ? 'Has existing event' : date}
                            >
                              {new Date(date + 'T00:00:00').getDate()}
                            </button>
                          );
                        })}
                      </div>

                      {selectedFutureDates.length > 0 && (
                        <div className="flex items-center justify-between bg-white p-3 rounded-lg border border-indigo-200">
                          <div className="text-sm text-indigo-900">
                            <span className="font-semibold">{selectedFutureDates.length}</span> future date{selectedFutureDates.length !== 1 ? 's' : ''} selected
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSelectedFutureDates([])}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            >
                              Clear
                            </button>
                            <button
                              onClick={() => {
                                // Apply the average impact of all events to selected future dates
                                const avgImpact = events.reduce((sum, e) => sum + e.impactPercent, 0) / events.length;
                                selectedFutureDates.forEach(date => {
                                  const edit: typeof pendingEdits[0] = {
                                    type: 'dateRange',
                                    description: `Future event: ${date} (${avgImpact >= 0 ? '+' : ''}${Math.round(avgImpact)}%)`,
                                    startDate: date,
                                    endDate: date,
                                    editType: DateRangeEditType.ADJUST_BY_PERCENTAGE,
                                    value: Math.round(avgImpact),
                                    reason: `Based on historical events: ${events.map(e => e.name).join(', ')}`,
                                  };
                                  setPendingEdits(prev => [...prev, edit]);
                                });
                                setSelectedFutureDates([]);
                              }}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded transition-colors"
                            >
                              Apply to Selected Dates
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Adjustment Controls (shown for single_day and date_range modes) */}
              {(editMode === 'single_day' || editMode === 'date_range') && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-orange-900 mb-3">Adjustment Settings</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-orange-700 mb-1">Edit Type</label>
                      <select
                        value={editType}
                        onChange={(e) => setEditType(e.target.value as DateRangeEditType)}
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                      >
                        <option value={DateRangeEditType.ADJUST_BY_PERCENTAGE}>Adjust by %</option>
                        <option value={DateRangeEditType.SET_VALUE}>Set Value</option>
                        <option value={DateRangeEditType.ADJUST_BY_AMOUNT}>Adjust by Amount</option>
                        <option value={DateRangeEditType.MULTIPLY}>Multiply by</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-orange-700 mb-1">
                        {editType === DateRangeEditType.ADJUST_BY_PERCENTAGE ? 'Percentage' :
                         editType === DateRangeEditType.MULTIPLY ? 'Multiplier' : 'Value'}
                      </label>
                      <input
                        type="number"
                        value={adjustmentValue}
                        onChange={(e) => setAdjustmentValue(parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-orange-700 mb-1">Reason (optional)</label>
                      <input
                        type="text"
                        value={editReason}
                        onChange={(e) => setEditReason(e.target.value)}
                        placeholder="e.g., Marketing campaign"
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                      />
                    </div>
                  </div>

                  {/* Preview */}
                  {(selectedIntervals.length > 0 || editMode === 'date_range') && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200">
                      <div className="text-xs font-medium text-orange-700 mb-2">Preview</div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-gray-500">Affected Intervals</div>
                          <div className="font-bold text-gray-900">{previewStats.affectedIntervals}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Original Total</div>
                          <div className="font-bold text-gray-900">{previewStats.originalTotal.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Adjusted Total</div>
                          <div className="font-bold text-gray-900">{previewStats.adjustedTotal.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-gray-500">Change</div>
                          <div className={`font-bold ${parseFloat(previewStats.percentChange) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {parseFloat(previewStats.percentChange) >= 0 ? '+' : ''}{previewStats.percentChange}%
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={addPendingEdit}
                    disabled={editMode === 'single_day' ? selectedIntervals.length === 0 : false}
                    className={`mt-4 w-full py-2 text-sm font-medium rounded-lg transition-colors ${
                      (editMode === 'single_day' && selectedIntervals.length === 0)
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                  >
                    Add to Pending Edits
                  </button>
                </div>
              )}

              {/* Pending Edits */}
              {pendingEdits.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-3">
                    Pending Edits ({pendingEdits.length})
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {pendingEdits.map((edit, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-white rounded border border-green-200"
                      >
                        <div className="flex items-center space-x-2">
                          <Check className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-gray-900">{edit.description}</span>
                        </div>
                        <button
                          onClick={() => removePendingEdit(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Apply to future days option */}
                  <label className="flex items-center space-x-2 mt-4 text-sm text-green-800">
                    <input
                      type="checkbox"
                      checked={applyToFutureDays}
                      onChange={(e) => setApplyToFutureDays(e.target.checked)}
                      className="rounded border-green-300 text-green-600 focus:ring-green-500"
                    />
                    <span>Apply these patterns to matching days in the future</span>
                  </label>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
              <button
                onClick={reset}
                disabled={pendingEdits.length === 0}
                className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  pendingEdits.length > 0
                    ? 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                <RotateCcw className="h-4 w-4" />
                <span>Clear All Edits</span>
              </button>

              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={pendingEdits.length === 0 || isSaving}
                  className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    pendingEdits.length > 0 && !isSaving
                      ? 'text-white bg-indigo-600 hover:bg-indigo-700'
                      : 'text-gray-400 bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  <Save className="h-4 w-4" />
                  <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
}
